import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LazySection from '@/components/base/LazySection';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface DbProduct {
  id: string;
  name: string;
  price: number;
  original_price: number | null;
  category: string | null;
  images: string[] | null;
  stock: number;
  sales: number;
  status: string;
}

const ShopPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [products, setProducts] = useState<DbProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>(['全部']);
  const [selectedCat, setSelectedCat] = useState(searchParams.get('category') || '全部');
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('综合');
  const [userPoints, setUserPoints] = useState<number>(0);

  // 加载商品
  const fetchProducts = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    const { data } = await supabase
      .from('products')
      .select('id, name, price, original_price, category, images, stock, sales, status')
      .eq('status', '上架')
      .order('sales', { ascending: false });
    if (data) {
      const list = data as DbProduct[];
      setProducts([...list]);
      const cats = Array.from(new Set(list.map((p) => p.category).filter(Boolean))) as string[];
      setCategories(['全部', ...cats]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProducts(true);

    const channel = supabase
      .channel('shop-products-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, (payload) => {
        console.log('[Shop Realtime] change received:', payload.eventType);
        fetchProducts(false);
      })
      .subscribe((status) => {
        console.log('[Shop Realtime] subscription status:', status);
      });

    return () => { supabase.removeChannel(channel); };
  }, [fetchProducts]);

  // 直接从DB读取最新积分
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('custom_users')
      .select('points')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setUserPoints(data.points);
      });
  }, [user?.id]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (selectedCat !== '全部') list = list.filter((p) => p.category === selectedCat);
    if (searchText.trim()) list = list.filter((p) => p.name.includes(searchText.trim()));
    if (sortBy === '积分↑') list.sort((a, b) => Number(a.price) - Number(b.price));
    else if (sortBy === '积分↓') list.sort((a, b) => Number(b.price) - Number(a.price));
    else if (sortBy === '销量') list.sort((a, b) => b.sales - a.sales);
    return list;
  }, [products, selectedCat, searchText, sortBy]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 max-w-[480px] mx-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center gap-3 px-4 h-14">
          <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
            <i className="ri-search-line text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="搜索积分商品..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-1 bg-transparent text-sm text-gray-700 outline-none"
            />
            {searchText && (
              <button onClick={() => setSearchText('')} className="cursor-pointer">
                <i className="ri-close-circle-fill text-gray-400 text-sm" />
              </button>
            )}
          </div>
          <button onClick={() => navigate('/cart')} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-shopping-cart-line text-gray-600 text-xl" />
          </button>
        </div>

        {/* My Points Bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-emerald-50">
          <div className="flex items-center gap-1.5">
            <i className="ri-copper-coin-line text-emerald-600 text-sm" />
            <span className="text-xs text-gray-600">我的积分：</span>
            <span className="text-emerald-600 font-bold text-sm">
              {user ? userPoints.toLocaleString() : '—'}
            </span>
          </div>
          <button onClick={() => navigate('/wallet')} className="text-xs text-emerald-600 cursor-pointer whitespace-nowrap">
            去赚积分 <i className="ri-arrow-right-s-line" />
          </button>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-0 overflow-x-auto scrollbar-hide px-4 py-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCat(cat)}
              className={`whitespace-nowrap text-sm px-3 py-1.5 rounded-full mr-2 cursor-pointer transition-colors ${
                selectedCat === cat ? 'bg-emerald-500 text-white font-medium' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center px-4 pb-2 gap-4 border-t border-gray-50">
          {['综合', '销量', '积分↑', '积分↓'].map((opt) => (
            <button
              key={opt}
              onClick={() => setSortBy(opt)}
              className={`text-sm py-1 cursor-pointer whitespace-nowrap ${sortBy === opt ? 'text-emerald-600 font-semibold' : 'text-gray-500'}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </header>

      {/* Product Grid */}
      <div className="px-4 pt-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="w-full aspect-square bg-gray-100" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <i className="ri-search-line text-5xl mb-3" />
            <p className="text-sm">暂无相关商品</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filtered.map((product, idx) => (
              <LazySection key={product.id} animation="fadeUp" delay={(idx % 4) * 60}>
                <div
                  onClick={() => navigate(`/product/${product.id}`)}
                  className="bg-white rounded-xl overflow-hidden cursor-pointer active:scale-95 transition-transform h-full"
                >
                  <div className="w-full aspect-square bg-gray-100 relative overflow-hidden">
                    <img
                      src={product.images?.[0] ?? ''}
                      alt={product.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                    {product.sales > 5000 && (
                      <span className="absolute top-2 left-2 bg-rose-500 text-white text-xs px-1.5 py-0.5 rounded-md font-medium">爆款</span>
                    )}
                    {product.stock === 0 && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">已售罄</span>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug mb-2">{product.name}</p>
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-emerald-600 font-bold text-lg">{Number(product.price).toLocaleString()}</span>
                        <span className="text-emerald-600 text-xs ml-0.5">积分</span>
                      </div>
                      {product.original_price && (
                        <span className="text-gray-400 text-xs line-through">{Number(product.original_price).toLocaleString()}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">已兑 {product.sales.toLocaleString()}</p>
                  </div>
                </div>
              </LazySection>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
